import { LeadFormDialog } from "@/components/LeadFormDialog";
import { useT, useLang } from "@/lib/i18n";
import { ArrowRight } from "lucide-react";
import { useState } from "react";

type BusinessTag = {
  en: string;
  zh: string;
};

type ResultTag = {
  en: string;
  zh: string;
};

type Diagnostic = {
  n: string;
  titleEn: string;
  titleZh: string;
  descEn: string;
  descZh: string;
  moduleEn: string;
  moduleZh: string;
};

type ModuleDetail = {
  nameEn: string;
  nameZh: string;
  claimEn: string;
  claimZh: string;
  problemEn: string;
  problemZh: string;
  supportEn: string[];
  supportZh: string[];
  changeEn: string;
  changeZh: string;
  fitEn: string;
  fitZh: string;
  keywordsEn: string[];
  keywordsZh: string[];
};

type Workflow = {
  n: string;
  titleEn: string;
  titleZh: string;
  descEn: string;
  descZh: string;
};

const SolutionsPage: React.FC = () => {
  const t = useT();
  const { lang } = useLang();
  const [activeModule, setActiveModule] = useState(0);
  const [formOpen, setFormOpen] = useState(false);

  const businessTags: BusinessTag[] = [
    { en: "Social Media Marketing", zh: "社媒营销" },
    { en: "Creative Advertising", zh: "创意广告" },
    { en: "Influencer Marketing", zh: "达人营销" },
    { en: "Brand Operations", zh: "品牌运营" },
    { en: "Public Opinion Management", zh: "舆情管理" },
    { en: "Search Optimization", zh: "搜索优化" },
  ];

  const resultTags: ResultTag[] = [
    { en: "Faster content production", zh: "内容生产更快" },
    { en: "More accurate creative testing", zh: "创意测试更准" },
    { en: "More consistent brand expression", zh: "品牌表达更稳" },
    { en: "Higher search visibility", zh: "搜索可见度更高" },
  ];

  const diagnostics: Diagnostic[] = [
    {
      n: "01",
      titleEn: "Growing demand for content",
      titleZh: "内容需求持续增长",
      descEn: "Brands need to continuously produce short videos, image-text posts, livestream materials, ad creatives, and platform-specific content. Traditional production methods can no longer support high-frequency updates.",
      descZh: "品牌需要持续输出短视频、图文、直播素材、广告素材和平台内容，传统生产方式难以支撑高频更新。",
      moduleEn: "Social Media Marketing",
      moduleZh: "社媒营销",
    },
    {
      n: "02",
      titleEn: "Shorter creative testing cycle",
      titleZh: "创意测试周期变短",
      descEn: "Advertising performance increasingly depends on a large number of creative versions. A single creative idea can hardly cover different platforms, audiences, and conversion goals.",
      descZh: "投放效果越来越依赖大量素材版本，单一创意难以覆盖不同平台、不同人群和不同转化目标。",
      moduleEn: "Creative Advertising",
      moduleZh: "创意广告",
    },
    {
      n: "03",
      titleEn: "Influencer collaboration is difficult to review",
      titleZh: "达人协同难以复盘",
      descEn: "Influencer selection, content briefs, publishing schedules, and performance reviews are scattered across different stages, making it difficult to form stable collaboration.",
      descZh: "达人选择、内容 Brief、发布节奏和效果复盘分散在不同环节，难以形成稳定协同。",
      moduleEn: "Influencer Marketing",
      moduleZh: "达人营销",
    },
    {
      n: "04",
      titleEn: "Brand assets are difficult to reuse",
      titleZh: "品牌资产难以复用",
      descEn: "Images, videos, scripts, campaign content, and advertising materials are scattered across different teams and projects, leading to high costs from repeated production.",
      descZh: "图片、视频、脚本、活动内容和投放素材散落在不同团队和项目中，重复制作成本高。",
      moduleEn: "Brand Operations",
      moduleZh: "品牌运营",
    },
    {
      n: "05",
      titleEn: "Search entry points are being reshaped",
      titleZh: "搜索入口正在重构",
      descEn: "The way users discover brands is expanding from traditional search to content platform search, social search, and AI Q&A. Brands need to rebuild their visibility.",
      descZh: "用户发现品牌的路径正在从传统搜索扩展到内容平台搜索、社交搜索和 AI 问答，品牌需要重新建设可见度。",
      moduleEn: "Search Optimization / Public Opinion Management",
      moduleZh: "搜索优化 / 舆情管理",
    },
  ];

  const moduleDetails: ModuleDetail[] = [
    {
      nameEn: "Social Media Marketing",
      nameZh: "社媒营销",
      claimEn: "Turn social media content from temporary output into a continuous growth content system.",
      claimZh: "让社媒内容从临时产出，变成持续增长的内容系统。",
      problemEn: "Brands need to continuously publish content on platforms such as Douyin, Xiaohongshu, WeChat Channels, and Instagram, but topic planning, scripts, visuals, publishing rhythm, and data review often lack a stable mechanism.",
      problemZh: "品牌需要持续在抖音、小红书、视频号、Instagram 等平台输出内容，但选题、脚本、视觉、发布节奏和数据复盘往往缺少稳定机制。",
      supportEn: [
        "Build content strategy and topic planning systems",
        "Generate short video scripts, social copy, and social media titles",
        "Assist in producing platform-adapted visual and video assets",
        "Plan publishing rhythm and support data review",
        "Accumulate reusable content templates and topic assets",
      ],
      supportZh: [
        "建立内容策略与选题体系",
        "生成短视频脚本、图文文案和社媒标题",
        "辅助生产平台适配的视觉与视频素材",
        "规划发布节奏并支持数据复盘",
        "沉淀可复用的内容模板和选题资产",
      ],
      changeEn: "Content production becomes more stable, topics and scripts become more systematic, content becomes better adapted to different platforms, and publishing and review form a closed loop.",
      changeZh: "内容生产更稳定，选题和脚本更系统，不同平台内容更适配，发布与复盘形成闭环。",
      fitEn: "Brands with continuous content operation needs; teams that need to improve social content efficiency; marketing teams that want to build a content growth mechanism.",
      fitZh: "有持续内容运营需求的品牌；需要提升社媒内容效率的团队；希望建立内容增长机制的营销团队。",
      keywordsEn: ["Topic Planning", "Script Generation", "Platform Adaptation", "Data Review"],
      keywordsZh: ["选题体系", "脚本生成", "平台适配", "数据复盘"],
    },
    {
      nameEn: "Creative Advertising",
      nameZh: "创意广告",
      claimEn: "Quickly expand one creative idea into multiple ad materials that can be tested, iterated, and launched.",
      claimZh: "把一个创意，快速扩展成可测试、可迭代、可投放的多版本广告素材。",
      problemEn: "Advertising requires more and more creative materials. The same creative direction often needs to be split into multiple versions to fit different platforms, audiences, selling points, and conversion goals.",
      problemZh: "广告投放对创意素材的需求越来越大。同一个创意方向，往往需要拆成多个版本，以适配不同平台、人群、卖点和转化目标。",
      supportEn: [
        "Generate advertising creative directions",
        "Deconstruct viral content structures",
        "Analyze the first three seconds of video hooks",
        "Generate video scripts for different selling points and audiences",
        "Reuse effective content structures and rewrite them into new materials",
        "Batch-produce creative variants",
        "Support ad testing and creative review",
      ],
      supportZh: [
        "生成广告创意方向",
        "拆解爆款内容结构",
        "分析视频前三秒 Hook",
        "针对不同卖点和人群生成视频脚本",
        "复用有效内容结构，快速改写为新素材",
        "批量生产素材变体",
        "支持投放测试与创意复盘",
      ],
      changeEn: "Creative materials become more diverse, testing becomes faster, creative directions become richer, and media buying decisions become more evidence-based.",
      changeZh: "素材版本更多样，测试速度更快，创意方向更丰富，投放决策更有依据。",
      fitEn: "Brands with high-frequency ad campaigns; teams that need large volumes of short video assets; performance marketing teams that want to improve creative testing efficiency.",
      fitZh: "高频投放广告的品牌；需要大量短视频素材的团队；希望提升广告素材测试效率的投放团队。",
      keywordsEn: ["Hook", "Script", "Creative Variants", "Ad Testing"],
      keywordsZh: ["Hook", "脚本", "素材变体", "投放测试"],
    },
    {
      nameEn: "Influencer Marketing",
      nameZh: "达人营销",
      claimEn: "Upgrade influencer collaboration from resource purchasing to planned, collaborative, and reviewable content marketing actions.",
      claimZh: "让达人合作从资源采购，升级为可规划、可协同、可复盘的内容营销动作。",
      problemEn: "Influencer selection relies heavily on experience, content brief execution is unstable, content styles vary across influencers, and communication results are difficult to review.",
      problemZh: "依赖经验选择达人，内容 Brief 执行不稳定，不同达人的内容风格不统一，传播效果难复盘。",
      supportEn: [
        "Analyze influencer profiles and content styles",
        "Develop influencer matching strategies",
        "Design content briefs",
        "Co-create content based on influencer style",
        "Plan publishing rhythm",
        "Track impressions, engagement, comment feedback, and conversion performance",
        "Accumulate effective influencers and content formats",
      ],
      supportZh: [
        "分析达人画像和内容风格",
        "制定达人匹配策略",
        "设计内容 Brief",
        "结合达人风格进行内容共创",
        "规划发布节奏",
        "追踪曝光、互动、评论反馈和转化表现",
        "沉淀有效达人和内容形式",
      ],
      changeEn: "Influencer selection becomes more evidence-based, brief execution becomes more stable, content styles become more consistent, and collaboration results become easier to review.",
      changeZh: "达人选择更有依据，Brief 执行更稳定，内容风格更统一，合作效果更好复盘。",
      fitEn: "Brands that need large-scale influencer collaboration; teams with unstable seeding content quality; marketing teams that want to improve influencer collaboration efficiency and conversion performance.",
      fitZh: "需要批量达人合作的品牌；种草内容质量不稳定的团队；希望提升达人合作效率和转化表现的营销团队。",
      keywordsEn: ["Influencer Profiles", "Matching Strategy", "Content Brief", "Performance Review"],
      keywordsZh: ["达人画像", "匹配策略", "内容 Brief", "效果复盘"],
    },
    {
      nameEn: "Brand Operations",
      nameZh: "品牌运营",
      claimEn: "Make brand voice more consistent, content assets reusable, and long-term operations systematic.",
      claimZh: "让品牌声音更一致，让内容资产可沉淀，让长期运营有体系。",
      problemEn: "Brand positioning, visuals, copy, content, materials, and communication rhythm are scattered, resulting in inconsistent brand tone across platforms and low reusability of content assets.",
      problemZh: "品牌定位、视觉、文案、内容、素材和传播节奏分散，导致不同平台品牌调性不统一，内容资产难复用。",
      supportEn: [
        "Clarify brand narratives",
        "Build a brand content system",
        "Organize visual assets and marketing materials",
        "Unify cross-platform brand tone guidelines",
        "Plan content rhythm",
        "Build a digital asset reuse mechanism",
        "Enable effective content to support future marketing campaigns",
      ],
      supportZh: [
        "梳理品牌叙事",
        "搭建品牌内容体系",
        "整理视觉资产和营销素材",
        "统一跨平台品牌调性规范",
        "规划内容节奏",
        "建立数字资产复用机制",
        "让有效内容持续服务后续营销活动",
      ],
      changeEn: "Brand image becomes more consistent, marketing materials become easier to manage and reuse, cross-platform content is no longer fragmented, and long-term operations no longer depend on temporary projects.",
      changeZh: "品牌形象更一致，营销素材更容易管理和复用，不同平台内容不再割裂，长期运营不依赖临时项目。",
      fitEn: "Companies with inconsistent brand tone; teams with scattered multi-platform content assets; clients that want to build a long-term brand content system.",
      fitZh: "品牌调性不统一的企业；多平台内容资产分散的团队；希望建立长期品牌内容体系的客户。",
      keywordsEn: ["Brand Narrative", "Content Assets", "Visual Guidelines", "Reuse Mechanism"],
      keywordsZh: ["品牌叙事", "内容资产", "视觉规范", "复用机制"],
    },
    {
      nameEn: "Public Opinion Management",
      nameZh: "舆情管理",
      claimEn: "Move from passive risk handling to proactive market signal identification.",
      claimZh: "从被动处理风险，转向主动识别市场信号。",
      problemEn: "User feedback and brand risks are scattered across social media, content platforms, news, comment sections, and search results, making it difficult for brands to discover, assess, and respond in time.",
      problemZh: "用户反馈和品牌风险分散在社媒、内容平台、新闻、评论区和搜索结果中，品牌很难及时发现、判断和响应。",
      supportEn: [
        "Monitor public discussions",
        "Analyze user sentiment",
        "Identify risk signals",
        "Assess topic sources and communication trends",
        "Classify risk levels",
        "Provide response strategy recommendations",
        "Turn public opinion events into operational improvement inputs",
      ],
      supportZh: [
        "监测公开讨论",
        "分析用户情绪",
        "识别风险信号",
        "判断话题来源与传播趋势",
        "进行风险分级",
        "提供响应策略建议",
        "将舆情事件沉淀为运营改进依据",
      ],
      changeEn: "Problems are discovered earlier, user feedback becomes clearer, risk judgment becomes more evidence-based, and team response becomes more timely.",
      changeZh: "更早发现问题，用户反馈更清晰，风险判断更有依据，团队响应更及时。",
      fitEn: "Brands sensitive to public opinion risks; brands with high social media volume; teams that need timely access to market feedback and user sentiment.",
      fitZh: "对舆情风险敏感的品牌；社媒声量较高的品牌；需要及时掌握市场反馈和用户情绪的团队。",
      keywordsEn: ["Public Opinion Monitoring", "Sentiment Analysis", "Risk Alerts", "Response Recommendations"],
      keywordsZh: ["舆情监测", "情绪分析", "风险预警", "响应建议"],
    },
    {
      nameEn: "Search Optimization",
      nameZh: "搜索优化",
      claimEn: "Make the brand correctly visible at every stage of user search, questioning, and decision-making.",
      claimZh: "让品牌在用户搜索、提问和决策的每个环节，被正确看见。",
      problemEn: "Users now discover brands not only through traditional search engines, but also through content platform search, social search, and AI Q&A tools. If brand information is incomplete, inconsistent, or lacks authority, the brand may be ignored during the user's search and decision-making process.",
      problemZh: "用户发现品牌的路径已不限于传统搜索引擎，还包括内容平台搜索、社交搜索和 AI 问答工具。品牌信息若不完整、不一致、缺乏权重，就可能在用户搜索与决策过程中被忽略。",
      supportEn: [
        "Analyze keywords and user intent",
        "Optimize the structure of official websites, product pages, case pages, and Q&A pages",
        "Unify brand information across different platforms",
        "Optimize brand content in search results",
        "Improve search performance on content platforms",
        "Enhance brand visibility in AI Q&A scenarios",
        "Optimize the conversion path from search to consultation",
      ],
      supportZh: [
        "分析关键词与用户意图",
        "优化官网、产品页、案例页和问答页结构",
        "统一品牌在不同平台上的信息",
        "优化搜索结果中的品牌内容",
        "提升内容平台搜索表现",
        "增强品牌在 AI 问答场景中的可见度",
        "优化用户从搜索到咨询的转化路径",
      ],
      changeEn: "The brand becomes visible across more search entry points; users receive more accurate and consistent brand information; brand content can cover more real user questions.",
      changeZh: "品牌在更多搜索入口中被看见；用户接触到的品牌信息更准确、更一致；品牌内容能覆盖更多用户真实问题。",
      fitEn: "Companies that need to improve brand visibility; brands with weak content platform search performance; brands that want to build presence in AI search and Q&A scenarios.",
      fitZh: "需要提升品牌可见度的企业；内容平台搜索表现较弱的品牌；希望布局 AI 搜索和问答场景的品牌。",
      keywordsEn: ["Keywords", "Content Structure", "Platform Search", "AI Visibility"],
      keywordsZh: ["关键词", "内容结构", "平台搜索", "AI 可见性"],
    },
  ];

  const workflow: Workflow[] = [
    {
      n: "01",
      titleEn: "Insights",
      titleZh: "洞察",
      descEn: "Analyze the brand, users, platforms, competitors, content trends, and market feedback to identify growth opportunities and key problems.",
      descZh: "分析品牌、用户、平台、竞品、内容趋势和市场反馈，找到增长机会与关键问题。",
    },
    {
      n: "02",
      titleEn: "Generate",
      titleZh: "生成",
      descEn: "Use AI to assist in generating scripts, copy, visuals, videos, ad creatives, content topics, and marketing assets.",
      descZh: "利用 AI 辅助生成脚本、文案、视觉、视频、广告素材、内容选题和营销资产。",
    },
    {
      n: "03",
      titleEn: "Test",
      titleZh: "测试",
      descEn: "Conduct multi-version testing across different platforms, audiences, selling points, and creative directions.",
      descZh: "围绕不同平台、不同人群、不同卖点和不同创意方向进行多版本测试。",
    },
    {
      n: "04",
      titleEn: "Replicate",
      titleZh: "复刻",
      descEn: "Turn better-performing content structures, creative methods, material templates, and communication paths into repeatable methods.",
      descZh: "将表现更好的内容结构、创意方式、素材模板和传播路径转化为可重复执行的方法。",
    },
    {
      n: "05",
      titleEn: "Accumulate",
      titleZh: "沉淀",
      descEn: "Accumulate effective materials, scripts, visuals, data, briefs, cases, and project experience as brand assets.",
      descZh: "把有效素材、脚本、视觉、数据、Brief、案例和项目经验沉淀为品牌资产。",
    },
    {
      n: "06",
      titleEn: "Reuse",
      titleZh: "复用",
      descEn: "Allow accumulated content assets, creative templates, and working methods to continue supporting future marketing campaigns.",
      descZh: "让沉淀下来的内容资产、创意模板和工作方法继续服务后续营销活动。",
    },
  ];

  const activeModuleData = moduleDetails[activeModule];

  return (
    <div className="landing-v2 min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="flex flex-col items-center text-center max-w-6xl">
            {/* Platform badge */}
            <div className="inline-flex items-center gap-3 glass px-4 py-2 text-xs text-muted-foreground mb-8">
              <span className="h-px w-8 bg-[hsl(var(--brand))]" />
              {t("End-to-End AI Marketing Solution", "AI 营销全链路解决方案")}
            </div>

            {/* Main title */}
            <h1 className="text-6xl md:text-7xl lg:text-10xl font-semibold tracking-tight leading-[1.05]">
              {t( 
                <>
                  <span className="text-6xl md:text-7xl">Make brand growth more</span><span className="italic brand-text text-6xl md:text-7xl"> predictable</span>
                </>,
                <>
                  让品牌增长<span className="italic brand-text">更具确定性</span>
                </>,
              )}
            </h1>

            {/* Result tags */}
            <div className={`${lang !== "zh" ? "mt-8 md:mt-10" : "mt-10 md:mt-14"} flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs tracking-[0.2em] uppercase text-muted-foreground/100`}>
              {resultTags.map((tag, i) => (
                <span key={i} className="flex items-center gap-5">
                  {t(tag.en, tag.zh)}
                  {i < resultTags.length - 1 && (
                    <span className="h-px w-3 bg-border/100" />
                  )}
                </span>
              ))}
            </div>

            {/* Subtitle */}
            <p className={`${lang !== "zh" ? "mt-8 md:mt-10" : "mt-12 md:mt-16"} max-w-2xl text-base md:text-lg text-muted-foreground/90 leading-relaxed`}>
              {t(
                "Oran AI helps brands upgrade from experience-driven marketing execution to an AI- and data-driven growth system.",
                "橙果视界帮助品牌从经验驱动的营销执行，升级为 AI 数据驱动的增长系统。",
              )}
            </p>
            <p className={`${lang !== "zh" ? "mt-3" : "mt-5"} max-w-2xl text-sm md:text-base text-muted-foreground/100 leading-[1.8]`}>
              {t(
                "Built around social media marketing, creative advertising, influencer marketing, brand operations, public opinion management, and search optimization, Oran AI connects scattered marketing actions into an intelligent marketing system that can produce, test, accumulate, and reuse growth assets.",
                "围绕社媒营销、创意广告、达人营销、品牌运营、舆情管理与搜索优化，橙果视界将分散的营销动作连接成一套可生产、可测试、可沉淀、可复用的智能营销体系。",
              )}
            </p>
          </div>
        </div>

        {/* Business tags */}
        <div className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs tracking-[0.15em] text-muted-foreground/75">
            {businessTags.map((tag, i) => (
              <span key={i} className="flex items-center gap-8">
                {t(tag.en, tag.zh)}
                {i < businessTags.length - 1 && (
                  <span className="h-1 w-1 rounded-full bg-border/100" />
                )}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Bottlenecks Section */}
      <section className="border-t border-border"> 
        <div className="mx-auto max-w-6xl w-full px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            {/* Section badge */}
            <div className="inline-flex items-center gap-3 text-xs text-muted-foreground/100 mb-6">
              <span className="h-px w-8 bg-[hsl(var(--brand))]" />
              {t("New Bottlenecks in Brand Growth", "品牌增长的新瓶颈")}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] max-w-[18ch]">
              {t(
                "More channels, faster cycles, and greater uncertainty.",
                "渠道更多，节奏更快，不确定性也更高。",
              )}
            </h2>
            <p className="mt-6 text-sm md:text-base text-muted-foreground/80 leading-[1.8] max-w-2xl">
              {t(
                "Today's brand competition is no longer just about a single campaign. It is about whether a brand can produce faster, test faster, review faster, and continuously accumulate effective experience across multiple platforms and content cycles.",
                "今天的品牌竞争，不再只是单次 Campaign 的竞争，而是能否在多个平台、多个内容周期中，更快生产、更快测试、更快复盘，并持续沉淀有效经验。",
              )}
            </p>
          </div>

          {/* Diagnostics list */}
          <div className="mt-14 md:mt-18 border-t border-border">
            {diagnostics.map((d) => (
              <div
                key={d.n}
                className="border-b border-border py-8 md:py-10 grid grid-cols-12 gap-4 md:gap-8 items-start"
              >
                <div className="col-span-2 md:col-span-1">
                  <div className="text-muted-foreground/90 text-2xl md:text-3xl lg:text-4xl font-semibold">
                    {d.n}
                  </div>
                </div>
                <div className="col-span-10 md:col-span-4">
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-2">
                    {t(d.moduleEn, d.moduleZh)}
                  </div>
                  <div className="text-lg md:text-xl lg:text-2xl font-medium">
                    {t(d.titleEn, d.titleZh)}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <p className="text-sm md:text-base leading-[1.8] text-muted-foreground/85">
                    {t(d.descEn, d.descZh)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom statement */}
          <div className="pt-24 md:pt-32 flex justify-center">
            <p className="text-center max-w-3xl leading-[1.4] text-2xl md:text-3xl lg:text-4xl font-medium">
              {t(
                <>
                  What brands need is not just more execution roles, but an AI growth system that turns every marketing action into{" "}
                  <span className="brand-text">reusable experience and assets</span>.
                </>,
                <>
                  品牌需要的不只是更多的执行角色，而是一套能让每一次营销动作
                  <span className="brand-text">沉淀为经验和资产</span>
                  的 AI 增长系统。
                </>,
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Module Details Section */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl w-full px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            {/* Section badge */}
            <div className="inline-flex items-center gap-3 text-xs text-muted-foreground/100 mb-6">
              <span className="h-px w-8 bg-[hsl(var(--brand))]" />
              {t("Six Business Modules", "六大业务模块")}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] max-w-[16ch]">
              {t(
                "AI marketing capabilities covering the key chain of brand growth.",
                "覆盖品牌增长关键链路的 AI 营销能力。",
              )}
            </h2>
            <p className="mt-6 max-w-2xl text-sm text-muted-foreground/80 leading-[1.8]">
              {t(
                "The six modules can solve individual business problems independently, or be combined into a complete AI marketing solution based on the brand's stage and growth goals.",
                "六大模块既可以独立解决单一业务问题，也可以根据品牌阶段和增长目标组合成完整的 AI 营销解决方案。",
              )}
            </p>
          </div>

          {/* Module tabs and content */}
          <div className="mt-14 md:mt-18 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Tabs */}
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="flex lg:block overflow-x-auto lg:overflow-visible -mx-6 lg:mx-0 px-6 lg:px-0 gap-0 lg:gap-0">
                  {moduleDetails.map((mod, i) => (
                    <button
                      key={mod.nameEn}
                      onClick={() => setActiveModule(i)}
                      className={`relative lg:w-full text-left whitespace-nowrap lg:whitespace-normal border-b border-border lg:border-r lg:border-b-0 lg:border-l lg:border-t-0 lg:py-5 py-3 px-4 lg:pl-5 lg:pr-3 transition-colors group ${
                        i === activeModule ? "bg-background/50" : "hover:bg-background/30"
                      }`}
                    >
                      {i === activeModule && (
                        <span className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 h-8 w-0.5 bg-[hsl(var(--brand))]" />
                      )}
                      <div className="flex items-baseline gap-3">
                        <span
                          className={`text-xs tracking-[0.2em] uppercase ${
                            i === activeModule ? "brand-text" : "text-muted-foreground/55"
                          }`}
                        >
                          0{i + 1}
                        </span>
                        <span
                          className={`text-sm md:text-base ${
                            i === activeModule
                              ? "text-foreground"
                              : "text-muted-foreground/65 group-hover:text-muted-foreground/90"
                          }`}
                        >
                          {t(mod.nameEn, mod.nameZh)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Detail content */}
            <div
              key={activeModule}
              className="lg:col-span-8 animate-in fade-in duration-500 max-w-2xl"
            >
              <h3 className="text-foreground leading-[1] text-3xl md:text-4xl lg:text-5xl font-semibold">
                {t(activeModuleData.nameEn, activeModuleData.nameZh)}
              </h3>
              <p className="mt-5 text-muted-foreground/90 leading-[1.4] text-lg md:text-xl lg:text-2xl font-medium">
                {t(activeModuleData.claimEn, activeModuleData.claimZh)}
              </p>

              {/* Keywords */}
              <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-xs tracking-[0.15em] uppercase text-muted-foreground/100">
                {activeModuleData.keywordsEn.map((k, i) => (
                  <span key={i} className="flex items-center gap-4">
                    {t(k, activeModuleData.keywordsZh[i])}
                    {i < activeModuleData.keywordsEn.length - 1 && (
                      <span className="h-px w-3 bg-border/100" />
                    )}
                  </span>
                ))}
              </div>

              {/* Problem */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-3">
                  {t("Problem", "问题")}
                </div>
                <p className="text-sm md:text-base leading-[1.8] text-muted-foreground/85">
                  {t(activeModuleData.problemEn, activeModuleData.problemZh)}
                </p>
              </div>

              {/* Support */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-3">
                  {t("How We Help", "我们提供的支持")}
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {activeModuleData.supportEn.map((s, i) => (
                    <li
                      key={i}
                      className="flex gap-3 text-sm text-muted-foreground/90 leading-[1.7]"
                    >
                      <span className="mt-2 h-px w-2 bg-border/45 flex-shrink-0" />
                      <span>{t(s, activeModuleData.supportZh[i])}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Change and Fit */}
              <div className="mt-6 pt-6 border-t border-border grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-3">
                    {t("What Changes", "带来什么变化")}
                  </div>
                  <p className="text-sm leading-[1.75] text-muted-foreground/85">
                    {t(activeModuleData.changeEn, activeModuleData.changeZh)}
                  </p>
                </div>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground/60 mb-3">
                    {t("Suitable For", "适合客户")}
                  </div>
                  <p className="text-sm leading-[1.75] text-muted-foreground/85">
                    {t(activeModuleData.fitEn, activeModuleData.fitZh)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl w-full px-6 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            {/* Section badge */}
            <div className="inline-flex items-center gap-3 text-xs text-muted-foreground/100 mb-6">
              <span className="h-px w-8 bg-[hsl(var(--brand))]" />
              {t("AI Marketing Workflow", "AI 营销工作流")}
            </div>
            <h2 className="mx-auto max-w-[14ch] text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1]">
              {t(
                "Turn one-time execution into reusable growth cycles.",
                "把一次性执行，变成可复用的增长循环。",
              )}
            </h2>
            <p className="mt-6 text-sm text-muted-foreground/80 leading-[1.8]">
              {t(
                "Oran AI breaks brand marketing into executable, testable, reviewable, and accumulable workflows, enabling teams to continuously accumulate effective experience across content, advertising, influencers, brand, public opinion, and search scenarios.",
                "橙果视界将品牌营销拆解为可执行、可测试、可复盘、可沉淀的工作流，让团队在内容、广告、达人、品牌、舆情和搜索场景中持续积累有效经验。",
              )}
            </p>
          </div>

          {/* Horizontal flow (desktop) */}
          <div className="mt-16 md:mt-20 hidden md:block">
            <div className="pt-8">
              <div className="grid grid-cols-6 gap-3 relative">
                {/* connector line */}
                {workflow.map((w, i) => (
                  <div key={w.n} className="relative flex flex-col items-start">
                    <div className="flex items-center w-full">
                      <span className="text-foreground bg-background pr-2.5 text-2xl font-semibold">
                        {w.n}
                      </span>
                      {i < workflow.length - 1 && (
                        <span className="ml-auto text-muted-foreground/45 text-sm pl-2 bg-background">
                          →
                        </span>
                      )}
                    </div>
                    <h3 className="mt-5 text-foreground text-base md:text-lg font-medium">
                      {t(w.titleEn, w.titleZh)}
                    </h3>
                    <p className="mt-3 text-xs md:text-sm leading-[1.7] text-muted-foreground/80 pr-3">
                      {t(w.descEn, w.descZh)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vertical timeline (mobile) */}
          <div className="md:hidden mt-14 border-t border-border">
            {workflow.map((w) => (
              <div
                key={w.n}
                className="border-b border-border py-6 grid grid-cols-12 gap-3 items-baseline"
              >
                <div className="col-span-2">
                  <div className="text-muted-foreground/90 text-2xl font-semibold">
                    {w.n}
                  </div>
                </div>
                <div className="col-span-10">
                  <h3 className="text-foreground text-lg font-medium">{t(w.titleEn, w.titleZh)}</h3>
                  <p className="mt-2 text-sm leading-[1.75] text-muted-foreground/85">
                    {t(w.descEn, w.descZh)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom statement */}
          <div className="pt-16 md:pt-20 flex justify-center">
            <p className="text-center max-w-3xl leading-[1.4] text-2xl md:text-3xl lg:text-4xl font-medium">
              {t(
                <>
                  True AI marketing is not one-off output. It is about making every marketing action the{" "}
                  <span className="brand-text">starting point for the next round of growth</span>.
                </>,
                <>
                  真正的 AI 营销，不是一次性输出，而是让每一次营销动作都成为
                  <span className="brand-text">下一次增长的起点</span>
                  。
                </>,
              )}
            </p>
          </div>
        </div>
      </section>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button
              className="btn-primary"
              onClick={() => setFormOpen(true)}
            >
              {t("Contact Us", "联系我们")} <ArrowRight size={16} />
            </button>
          </div>

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default SolutionsPage;
